<template>
  <form @submit.prevent>
    <div>
      <h2>
        <span v-if="isEditMode">{{ $t("policy.name") }} "{{ editedEntity.name | translate }}" {{ $t("common.update") }}</span>
        <span v-else>{{ $t("policy.new") }}</span>
      </h2>

      <div class="row">
        <div class="col-md-4">
          <h6 class="mt-3">{{ $t("policy-category.text") }}</h6>
          <select class="form-control mb-3" v-model="form.category">
            <option value="ADMINISTRATIVE">{{ $t("policy-category.administrative") }}</option>
            <option value="TECHNICAL">{{ $t("policy-category.technical") }}</option>
            <option value="LEGAL">{{ $t("policy-category.legal") }}</option>
          </select>

          <b-form-checkbox switch v-model="form.isPeriodical" :value="true" :unchecked-value="false" class="custom-checkbox mb-3 mt-4">
            {{ $t("policy.is-periodical") }}
          </b-form-checkbox>
        </div>
        <div class="col-md-8">
          <sweet-input
            class="mt-3"
            v-model="form.name"
            :isValid="!$v.form.name.$error"
            @input="$v.form.name.$touch()"
            layout="vertical"
            :placeholder="$t('policy.name')"
            :label="$t('policy.name')"
          />
          <sweet-input
            class="mt-1"
            v-if="form.isPeriodical"
            :isValid="isPeriodValid"
            v-model="form.period"
            type="number"
            :min="1"
            layout="vertical"
            :placeholder="$t('policy.period')"
            :label="$t('policy.period')"
          />
        </div>
      </div>

      <hr />

      <div class="modal-question-card p-2" style="background-color: white !important;">
        <right-panel class="float-right" id="sidebar-new-policy-group" width="800px" :icon="'mdi mdi-plus'" :text="$t('policy-group.new')" @hidden="rightPanelClosed">
          <div class="pl-5 pr-5">
            <policy-group-form :hide-library="true" />
          </div>
        </right-panel>
        <table class="table table-bordered table-striped bg-white">
          <thead class="bg-secondary text-white">
            <tr>
              <td class="w-15">{{ $t("policy.compliance") }}</td>
              <td class="w-25">{{ $t("policy-group.text") }}</td>
              <td class="w-10">{{ $t("policy.no") }}</td>
              <td class="w-10">{{ $t("policy.level") }}</td>
              <td class="w-25">{{ $t("policy.definition") }}</td>
              <td class="w-5"></td>
            </tr>
            <tr>
              <td colspan="6" class="bg-light">
                <p v-if="hasRequiredProduct" class="text-danger">{{ $t("policy.required-product-alert", { product: currentProduct || "PLATFORM" }) }}</p>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(compliance, index) in form.compliances" :key="compliance._id">
              <td>{{ compliance.product }}</td>
              <td>{{ compliance.policyGroup.name | translate }}</td>
              <td>{{ compliance.policyNo }}</td>
              <td>{{ compliance.policyLevel }}</td>
              <td>{{ compliance.policyDefinition | translate }}</td>
              <td><i @click="removeCompliance(index)" class="bx bx-trash-alt text-danger hand" v-b-tooltip.hover :title="$t('common.delete')"></i></td>
            </tr>
            <tr>
              <td>
                <select class="form-control " v-model="compliance.product">
                  <option :value="undefined">{{ $t("common.clear-selection") }}</option>
                  <option v-for="item in policySuitableProducts" :key="item" :value="item">{{ item }}</option>
                </select>
              </td>
              <td>
                <filter-select
                  id="policyGroupFilter"
                  ref="policyGroupFilter"
                  :placeholder="$t('policy-group.text')"
                  :selected="compliance.policyGroup"
                  @filter="onPolicyGroupSearch"
                  @changed="onPolicyGroupSelect"
                />
              </td>
              <td><sweet-input v-model="compliance.policyNo" :isValid="true" /></td>
              <td>
                <select class="form-control mb-3" v-model="compliance.policyLevel">
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                </select>
              </td>
              <td><sweet-textarea v-model="compliance.policyDefinition" :isValid="true" :rows="5" /></td>
              <td>
                <b-button :disabled="this.$v.compliance.$error" class="mr-2" variant="main" @click="addCompliance()">{{ $t("common.save") }}</b-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="modal-question-card p-2 mt-4" style="background-color: white !important;">
        <div style="width: 50%;">
          <h6 class="mt-4">
            {{ $t("risk.plural") }}
            <right-panel class="float-right" id="sidebar-new-risk-definition" width="800px" :icon="'mdi mdi-plus'" :text="$t('risk.definition.new')" @hidden="rightPanelClosed">
              <div class="pl-5 pr-5">
                <risk-definition-form :hide-library="true" />
              </div>
            </right-panel>
          </h6>

          <filter-select
            id="riskFilter"
            ref="riskFilter"
            :placeholder="$t('risk.text')"
            :multiple="true"
            :selected="form.riskDefinitions"
            @filter="onRiskSearch"
            @changed="onRiskSelect"
          />

          <!-- <b-button v-if="selectedRisks" @click="addRisk" variant="link">
                <i class="bx bx-plus"></i>
                {{ $t("question.link-with-risk") }}
              </b-button> -->
        </div>
      </div>

      <div class="d-flex mb-3" style="gap:1rem">
        <div class="mt-3" style="width: 50%;">
          <h6 class="mt-2">{{ $t("task.plural") }}</h6>
          <div v-for="task in form.taskDefinitions" :key="task._id" class="card card-body">
            <sweet-button :only-icon="true" variant="false" icon="mdi mdi-delete" buttonClass="text-danger hidden float-right" @click="removeTaskItem(task)" />
            <sweet-button :only-icon="true" variant="false" icon="mdi mdi-pencil" buttonClass="text-warning hidden float-right" @click="editTaskItem(task)" />

            <h6>{{ $t("task.name") }}</h6>
            {{ task.name | translate }}
            <h6 class="mt-2">{{ $t("task.description") }}</h6>
            {{ task.description | translate }}

            <h6 class="mt-2">{{ $t("role.text") }}</h6>
            <div v-if="task.role && task.directAssign">
              <span class="badge badge-soft-primary">
                {{ task.role.name | translate }}
              </span>
            </div>
            <h6>{{ $t("task.assignee-options.preference") }}</h6>
            <span v-if="task.assignToCausedAccount">{{ $t("system-flow.assign.caused-account") }} </span>
            <span v-if="task.assignToOrganizationOwner">{{ $t("task.assignee-options.organization-owner") }} </span>
            <span v-if="task.directAssign">{{ $t("task.assignee-options.selected-role") }} </span>
            <div v-if="task.blockedByTasks && task.blockedByTasks.length > 0">
              <h6>{{ $t("task.blocked-by") }}</h6>
              <ol>
                <li v-for="t in task.blockedByTasks" :key="t._id">{{ t.name | translate }}</li>
              </ol>
            </div>
          </div>

          <policy-task-definition-form store="organizationPolicy" />
        </div>
      </div>

      <hr />

      <sweet-button
        @click="save()"
        type="submit"
        :loading="isUpdating"
        :disabled="isUpdating || $v.form.$invalid || !isPeriodValid || !isRequiredComplianceAdded"
        variant="success"
        icon="mdi mdi-check"
        size="sm"
        buttonClass="mt-2"
      >
        {{ $t("common.save") }}
      </sweet-button>
    </div>
  </form>
</template>

<script>
import SweetTextarea from "@c/form/sweet-textarea";
import { maxLength, minLength, required } from "vuelidate/lib/validators";
import RiskDefinitionForm from "../risk/organization-risk-definition-form";
import PolicyTaskDefinitionForm from "./component/policy-task-definition-form";
import PolicyGroupForm from "./organization-policy-group-form";

const state = "organizationPolicy";

export default {
  components: {
    SweetTextarea,
    PolicyTaskDefinitionForm,
    RiskDefinitionForm,
    PolicyGroupForm,
  },
  data() {
    return {
      form: {
        name: undefined,
        category: "ADMINISTRATIVE",
        conditions: [],
        compliances: [],
        period: 0,
        riskDefinitions: [],
        taskDefinitions: [],
      },
      compliance: {
        product: undefined,
        policyGroup: undefined,
        policyNo: undefined,
        policyLevel: 1,
        policyDefinition: undefined,
      },
      condition: {
        _id: undefined,
        type: "ON_PRODUCT_ENABLED",
        params: {
          products: [],
          assetGroupCategory: undefined,
        },
      },

      selectedRisks: [],

      formError: undefined,
    };
  },
  validations: {
    form: {
      name: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(350),
      },
      taskDefinitions: {
        required,
        minLength: minLength(1),
      },
      category: {
        required,
      },
    },
    compliance: {
      product: {
        required,
      },
      policyGroup: {
        required,
      },
      policyLevel: {
        required,
      },
      policyNo: {
        required,
      },
      policyDefinition: {
        required,
      },
    },
  },

  watch: {
    selectedRisks: {
      deep: true,
      immediate: true,
      handler(v) {
        this.form.riskDefinitions = v;
      },
    },

    "form.compliances": {
      deep: true,
      immediate: true,
      handler(v, o) {
        if (!v || v?.length == 0) {
          this.$refs?.["riskFilter"]?.reset();
          // if (this.editedEntity) {
          //   this.form.riskDefinitions = this.editedEntity.riskDefinitions;
          // }
        }
      },
    },
    compliance: {
      deep: true,
      immediate: true,
      handler(n, o) {
        this.$v.compliance.$touch();
      },
    },
    "compliance.product": {
      deep: true,
      immediate: true,
      handler(n, o) {
        this.$refs?.["policyGroupFilter"]?.reset();
      },
    },
    editedEntity: {
      deep: true,
      immediate: true,
      handler(v) {
        if (v && Object.keys(v).length) {
          this.form = JSON.parse(JSON.stringify(this.prepareFormForEdit(v)));
        } else {
          this.resetFormValues();
        }
      },
    },
    tasks: {
      deep: true,
      immediate: true,
      handler(v) {
        if (v) {
          this.form.taskDefinitions = [...v];
        } else {
          this.form.taskDefinitions = [];
        }
        this.$forceUpdate();
        this.$v.form.$touch();
      },
    },
  },
  computed: {
    hasRequiredProduct() {
      return !this.form.compliances?.some((i) => i.product == (this.currentProduct || "PLATFORM"));
    },
    isRequiredComplianceAdded() {
      return this.form?.compliances?.some((i) => i.product == (this.currentProduct || "PLATFORM"));
    },
    currentOrganizationProducts() {
      return this.currentOrganization?.features?.accessableProducts || [];
    },
    filteredProducts() {
      return this.form.compliances?.length ? this.form?.compliances.map((i) => i.product) : [];
    },
    policySuitableProducts() {
      const ignoreList = ["RISK", "DDO"];

      return this.scopedProducts.filter((i) => !ignoreList.includes(i));
    },

    tasks() {
      return this.$store.getters[`${state}/tasks`];
    },
    isUpdating() {
      return this.$store.getters[`${state}/isUpdating`];
    },
    editedEntity() {
      return this.$store.getters[`${state}/editedEntity`];
    },
    isEditMode() {
      return this.$store.getters[`${state}/isEditMode`];
    },
    isPeriodValid() {
      if (this.form.period < 1 && this.form.isPeriodical) return false;
      return true;
    },
  },
  mounted() {},
  methods: {
    resetCompliance() {
      this.compliance.product = undefined;
      this.compliance.policyGroup = undefined;
      this.compliance.policyNo = undefined;
      this.compliance.policyDefinition = undefined;
    },

    addCompliance() {
      if (!this.form.compliances) {
        this.$set(this.form, "compliances", []);
      }
      if (this.form.compliances.some((i) => i.product === this.compliance.product)) {
        this.resetCompliance();
        return;
      }

      this.$set(this.form.compliances, this.form.compliances.length, {
        ...this.compliance,
        policyDefinition: {
          [this.currentLanguage]: this.compliance.policyDefinition,
        },
      });
      this.resetCompliance();
      this.$refs["riskFilter"]?.searchChanged("*");
      this.$forceUpdate();
    },
    includesAny(array, items) {
      return items.some((item) => array.includes(item));
    },
    removeCompliance(index) {
      this.form.compliances.splice(index, 1);
      this.form.riskDefinitions = this.form.riskDefinitions?.filter((i) => i.products.some((product) => this.filteredProducts.includes(product)));
      this.$forceUpdate();
    },

    resetFormValues() {
      this.form = {
        name: undefined,
        category: "ADMINISTRATIVE",
        conditions: [],
        compliances: [],
        period: 0,
        riskDefinitions: [],
        taskDefinitions: [],
      };

      this.compliance = {
        product: undefined,
        policyGroup: undefined,
        policyNo: undefined,
        policyLevel: 1,
        policyDefinition: undefined,
      };
      this.condition = {
        _id: undefined,
        type: "ON_PRODUCT_ENABLED",
        params: {
          products: [],
          assetGroupCategory: undefined,
        },
      };
      this.selectedRisks = [];
      this.$store.commit("organizationPolicy/tasks", undefined);
    },
    async save() {
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }

      try {
        const compliancesProducts = this.form.compliances.map((compliance) => compliance.product);
        if (!this.form.conditions || this.form.conditions.length == 0) {
          this.form.conditions = [
            {
              _id: "id" + new Date().getTime(),
              type: "ON_PRODUCT_ENABLED",
              params: {
                products: [...compliancesProducts],
              },
            },
          ];
        } else {
          const condition = this.form.conditions.find((condition) => condition.type === "ON_PRODUCT_ENABLED");
          if (!condition) {
            this.form.conditions.push({
              _id: "id" + new Date().getTime(),
              type: "ON_PRODUCT_ENABLED",
              params: {
                products: [...compliancesProducts],
              },
            });
          }
          const productEnabledConditionProducts = this.form.conditions.find((condition) => condition.type === "ON_PRODUCT_ENABLED").params.products;

          //eklenen ürünler
          compliancesProducts.forEach((cp) => {
            if (!productEnabledConditionProducts.includes(cp)) {
              const condition = this.form.conditions.find((condition) => condition.type === "ON_PRODUCT_ENABLED");

              if (condition) {
                condition.params.products.push(cp);
              }
            }
          });

          //silinen ürünler
          this.form.conditions = this.form.conditions.map((condition) => {
            condition.params.products = condition.params.products.filter((product) => compliancesProducts.includes(product));
            return condition;
          });
        }

        await this.$store.dispatch(`${state}/create`, this.form);

        this.resetFormValues();
        this.$toast.success(this.$t("common.success"));
        this.$root.$emit("bv::toggle::collapse", `sidebar-new-policy`);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    async onPolicyGroupSearch(value, callback) {
      if (!this.compliance.product) {
        return callback([]);
      }
      const response = await this.$store.dispatch(`libraryPolicyGroup/paginate`, {
        options: { limit: 200, sort: { [`name.${this.currentLanguage || tr}`]: "asc" } },
        query: {
          // "scope.value": { value: "system", isObject: true },
          name: { value },
          products: { value: this.compliance.product, isObject: true },
        },
      });

      callback(response.data?.docs ?? []);
    },
    onPolicyGroupSelect(value) {
      this.compliance.policyGroup = value;
    },

    async onRiskSearch(value, callback) {
      if (this.form.compliances && this.form.compliances.length > 0) {
        const response = await this.$store.dispatch(`riskDefinition/paginate`, {
          options: { limit: 200 },
          query: {
            // "scope.value": { value: "system", isObject: true },
            // products: this.filteredProducts,
            $or: { value: this.filteredProducts.map((i) => ({ products: i })), isObject: true },
            name: { value },
          },
        });

        callback(response.data?.docs ?? []);
      } else {
        callback([]);
      }
    },

    onRiskSelect(riskDefinitions) {
      this.form.riskDefinitions = riskDefinitions;
    },
    // addRisk() {
    //   this.form.riskDefinitions = [...this.form.riskDefinitions, this.selectedRisk];
    //   this.selectedRisk = undefined;
    // },
    // removeRiskItem(risk) {
    //   this.form.riskDefinitions = [...this.form.riskDefinitions.filter((i) => i._id != risk._id)];
    // },
    removeTaskItem(task) {
      this.$store.commit(`${state}/removeTaskItem`, { task });
    },
    editTaskItem(task) {
      this.$store.commit(`${state}/editedTask`, { ...task });

      this.$root.$emit("bv::toggle::collapse", `sidebar-create-task`);
    },
    rightPanelClosed() {
      if (this.$refs.riskFilter) {
        this.$refs["riskFilter"]?.reset();
      }
    },
    closeTaskPanel() {
      this.$refs["policy-task-defition-form"]?.closeTaskPanel();
    },
  },
};
</script>
